/** The Practice Center — board picker + section groups of Teil cards
 * (the viobean grid: description, progress x/n, mastery ring). */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { myModules } from "../../api/auth";
import * as api from "../../api/exams";
import { useAuth } from "../../auth/AuthContext";
import { ContentLocked } from "../../components/ContentLocked";
import { Card, EmptyState, ProgressRing, Spinner } from "../../components/ui";
import { useT } from "../../i18n";
import { PracticeStatusCard } from "./PracticeStatusCard";
import { computeReadiness } from "./readiness";

export function PracticeCenterPage() {
  const t = useT();
  const { user } = useAuth();
  const [exams, setExams] = useState<api.ExamOverview[] | null>(null);
  const [modules, setModules] = useState<string[] | null>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      .listExams()
      .then((rows) => {
        if (!cancelled) setExams(rows);
      })
      .catch(() => {
        if (!cancelled) setExams([]);
      });
    // Which levels this account actually owns — so a locked level is obvious
    // here rather than after a click.
    myModules()
      .then((codes) => {
        if (!cancelled) setModules(codes);
      })
      .catch(() => {
        if (!cancelled) setModules([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Unknown while modules load — assume owned so nothing flashes a lock. */
  const owns = (level: string) =>
    modules === null || modules.includes(`content.${level.toLowerCase()}.practice`);

  if (exams === null) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner size={28} />
      </div>
    );
  }
  if (exams.length === 0) {
    return (
      <Card>
        <EmptyState message={t("learn.empty")} />
      </Card>
    );
  }

  const exam = exams[Math.min(active, exams.length - 1)];
  // Lead the picker with the exam board (telc / Goethe) as a top-level tab,
  // then the levels within the active board — the client asked for the boards
  // to be the main sections rather than a flat "telc B1, goethe B1" row.
  const boards = Array.from(new Set(exams.map((e) => e.board)));

  // Readiness = best-score average weighted by how much each part is
  // practised — a single "how ready am I?" number from existing data.
  const readiness = computeReadiness(exam);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold">{t("practice.title")}</h1>
          <p className="mt-1 text-muted">{t("practice.subtitle")}</p>
        </div>
        <div className="flex flex-col gap-2 sm:items-end">
          <div className="inline-flex self-start rounded-control border border-line p-0.5 sm:self-auto">
            {boards.map((board) => (
              <button
                key={board}
                onClick={() => setActive(exams.findIndex((e) => e.board === board))}
                className={`rounded-control px-3 py-1 text-sm font-semibold capitalize ${board === exam.board ? "bg-brand text-white" : "text-muted hover:text-ink"}`}
              >
                {board}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {exams.map((examOption, i) =>
              examOption.board === exam.board ? (
                <button
                  key={`${examOption.board}-${examOption.level}`}
                  onClick={() => setActive(i)}
                  className={`rounded-control border px-3 py-1.5 text-sm font-semibold ${i === active ? "border-brand bg-brand-soft text-brand-dark" : "border-line text-muted hover:text-ink"}`}
                >
                  {!owns(examOption.level) && <span aria-hidden>🔒 </span>}
                  {examOption.level}
                </button>
              ) : null,
            )}
          </div>
        </div>
      </div>

      <PracticeStatusCard onboarding={user?.onboarding} exam={exam} />

      {!owns(exam.level) ? (
        <ContentLocked module={`content.${exam.level.toLowerCase()}.practice`} />
      ) : (
        <>
      <Link
        to={`/practice/${exam.board}/${exam.level}/exam`}
        className="block rounded-card bg-grad-hero p-5 text-white shadow-card transition-all hover:-translate-y-0.5"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-extrabold">
              <span aria-hidden>📝</span> {t("practice.exam.cta")}
            </h2>
            <p className="mt-1 text-sm text-white/85">{t("practice.exam.ctaBody")}</p>
          </div>
          <span className="whitespace-nowrap rounded-control bg-white/20 px-3 py-1.5 text-sm font-semibold">
            {t("practice.exam.start")} →
          </span>
        </div>
      </Link>

      {readiness.state !== "none" && (
        <div className="rounded-card border border-line bg-surface p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase text-muted">
                {readiness.state === "ready"
                  ? t("practice.readiness.label")
                  : t("practice.readiness.avgLabel")}
              </p>
              <p className="text-2xl font-extrabold text-ink">
                {readiness.averageScore}%
                {readiness.band && (
                  <span className="ml-2 align-middle text-sm font-semibold text-muted">
                    {t(`practice.readiness.band.${readiness.band}`)}
                  </span>
                )}
              </p>
              <p className="text-xs text-muted">
                {t("practice.readiness.detail", {
                  practiced: readiness.practiced,
                  total: readiness.total,
                })}
              </p>
              {readiness.tasksToGo > 0 && (
                <p className="text-xs text-muted">
                  {t("practice.readiness.needMore", { count: readiness.tasksToGo })}
                </p>
              )}
              {readiness.untouchedSections.length > 0 && (
                <p className="text-xs text-muted">
                  {t("practice.readiness.needSections", {
                    sections: readiness.untouchedSections.join(", "),
                  })}
                </p>
              )}
            </div>
            <Link
              to={`/practice/${exam.board}/${exam.level}/review`}
              className="rounded-control border border-brand px-3 py-1.5 text-sm font-semibold text-brand hover:bg-brand-soft"
            >
              {t("practice.readiness.review")} →
            </Link>
          </div>
        </div>
      )}

      {exam.sections.map((section) => {
        const totalItems = section.parts.reduce((sum, part) => sum + part.item_count, 0);
        return (
          <section key={section.code}>
            <div className="mb-3 flex items-baseline justify-between">
              <h2 className="text-xl font-bold text-ink">
                <span aria-hidden>{section.icon}</span> {section.name}
                <span className="ml-2 text-sm font-normal text-muted">{section.english}</span>
              </h2>
              <span className="text-sm text-muted">
                {totalItems} {t("practice.practices")}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {section.parts.map((part) => {
                const body = (
                  <>
                    <h3 className="font-bold text-ink">{part.title}</h3>
                    <p className="mt-1 min-h-10 text-sm text-muted">{part.description}</p>
                    {/* Every part tracks progress now — objective parts are
                        server-scored, writing/speaking are AI-graded. */}
                    <div className="mt-3 flex items-end justify-between">
                      <div>
                        <p className="text-xs uppercase text-muted">{t("practice.progress")}</p>
                        <p className="text-lg font-bold text-ink">
                          {part.practiced} / {part.item_count}
                        </p>
                      </div>
                      <ProgressRing
                        percent={part.avg_best_score}
                        size={56}
                        label={`${part.code} mastery`}
                      />
                    </div>
                  </>
                );
                // The WHOLE tile is the link (viobean behavior), not just a
                // "Start" label. Empty parts render as a plain card.
                return part.item_count > 0 ? (
                  <Link
                    key={part.code}
                    to={`/practice/${exam.board}/${exam.level}/${part.code}`}
                    className="block"
                  >
                    <Card className="h-full transition-all hover:-translate-y-0.5 hover:border-brand">
                      {body}
                      <span className="mt-3 block text-sm font-semibold text-brand">
                        {t("practice.start")} →
                      </span>
                    </Card>
                  </Link>
                ) : (
                  <Card key={part.code} className="h-full opacity-80">{body}</Card>
                );
              })}
            </div>
          </section>
        );
      })}
        </>
      )}
    </div>
  );
}
